import CloseIcon from "@mui/icons-material/Close"
import {
    Box,
    IconButton,
    Link,
    Snackbar,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement } from "react"

interface IReleaseSnackbarProps {
    open: boolean
    setOpen: (open: boolean) => void
}

/**
 * Snackbar component that displays a message about a new release
 *
 * @param {boolean} open - whether the snackbar is open
 * @param {function} setOpen - function to set the state of the snackbar
 */
const ReleaseSnackbar = ({
    open,
    setOpen,
}: IReleaseSnackbarProps): ReactElement => {
    const theme = useTheme()
    const handleClose = (): void => {
        setOpen(false)
    }

    return (
        <Snackbar open={open} onClose={handleClose}>
            <Box
                sx={{
                    maxWidth: 480,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    boxShadow: theme.shadows[6],
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                        Help us improve Connect Direct
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
                        Take a short survey and tell us what matters to you.
                    </Typography>
                    <Link
                        href="https://forms.office.com/e/sisEpPYVsh"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            fontSize: "0.8125rem",
                        }}
                    >
                        Take the survey &rarr;
                    </Link>
                </Box>
                <IconButton
                    size="small"
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: "inherit",
                        opacity: 0.6,
                        "&:hover": { opacity: 1 },
                        mt: -0.5,
                        mr: -0.5,
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        </Snackbar>
    )
}

export default ReleaseSnackbar
